/** @format */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import "bootstrap/dist/css/bootstrap.min.css";
import "./LotStat.css";
import { useTranslation } from "react-i18next";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const LotStat = ({ lots, filteredLots, dateRangeFilter, onFilterClick }) => {
  const { t } = useTranslation();

  const mapping0 = {
    iCreatedDocs: "totalDocuments",
    iCompletedDocs: "realisesDocuments",
    iErroredDocs: "errorDocuments",
    iInvalidDocs: "invalidDocuments",
    iSuspectDocs: "suspectDocuments",
    iExtractingDocs: "extractingDocuments",
    iReviewingDocs: "reviewingDocuments",
  };

  // Fonction pour calculer les statistiques d'un array de lots
  const calculateStats = useCallback((lotsData) => {
    const stats = {};
    if (Array.isArray(lotsData) && lotsData.length > 0) {
      Object.keys(lotsData[0]).forEach((columnName) => {
        if (
          columnName.startsWith("i") &&
          columnName.endsWith("Docs") &&
          columnName in mapping0
        ) {
          stats[mapping0[columnName]] = 0;
        }
      });
    }

    lotsData.forEach((lot) => {
      stats.totalDocuments = (stats.totalDocuments || 0) + (parseInt(lot.iCreatedDocs) || 0);
      stats.realisesDocuments = (stats.realisesDocuments || 0) + (parseInt(lot.iCompletedDocs) || 0);
      stats.errorDocuments = (stats.errorDocuments || 0) + (parseInt(lot.iErroredDocs) || 0);
      stats.invalidDocuments = (stats.invalidDocuments || 0) + (parseInt(lot.iInvalidDocs) || 0);
      stats.suspectDocuments = (stats.suspectDocuments || 0) + (parseInt(lot.iSuspectDocs) || 0);
      stats.extractingDocuments = (stats.extractingDocuments || 0) + (parseInt(lot.iExtractingDocs) || 0);
      stats.reviewingDocuments = (stats.reviewingDocuments || 0) + (parseInt(lot.iReviewingDocs) || 0);
    });

    return stats;
  }, []);

  // Statistiques globales et filtrées
  const globalStats = useMemo(() => calculateStats(lots), [lots, calculateStats]);
  const filteredData = useMemo(() => filteredLots || lots, [filteredLots, lots]);
  const filteredStats = useMemo(() => calculateStats(filteredData), [filteredData, calculateStats]);

  // Détection si des filtres sont appliqués
  const hasFiltersApplied = useMemo(() => {
    if (!filteredLots) return false;
    if (filteredData.length !== lots.length) return true;
    // Vérifier aussi si dateRangeFilter est appliqué
    if (dateRangeFilter?.fromDate || dateRangeFilter?.toDate) return true;
    return false;
  }, [filteredLots, filteredData, lots, dateRangeFilter]);

  const statusCountsGlobal = useMemo(() => 
    lots.reduce((acc, lot) => {
      const status = lot.sStatus;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {}),
    [lots]
  );

  const statusCountsFiltered = useMemo(() => 
    filteredData.reduce((acc, lot) => {
      const status = lot.sStatus;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {}),
    [filteredData]
  );

  // Fonction pour créer les cartes fixes
  const createFixedCards = useCallback((stats, lotsCount, statsType = "global") => {
    return [
      {
        label: `${t("Total")} ${t("Lots")}`,
        value: lotsCount,
        filterKey: null,
        statsType,
      },
      {
        label: `${t("Total")} ${t("Documents")}`,
        value: stats.totalDocuments,
        statsType,
      },
      {
        label: `${t("Counter Documents")} ${t("In progress")}`,
        value: (stats.totalDocuments || 0) - (stats.realisesDocuments || 0),
        statsType,
      },
      {
        label: `${t("Counter Documents")} ${t("Completed")}`,
        value: stats.realisesDocuments,
        statsType,
      },
      {
        label: `${t("Counter Documents")} ${t("Error")}`,
        value: stats.errorDocuments,
        filterKey: "iErroredDocs",
        statsType,
      },
      {
        label: `${t("Counter Documents")} ${t("Invalids")}`,
        value: stats.invalidDocuments,
        filterKey: "iInvalidDocs",
        statsType,
      },
      {
        label: `${t("Counter Documents")} ${t("Suspect")}`,
        value: stats.suspectDocuments,
        filterKey: "iSuspectDocs",
        statsType,
      },
      {
        label: `${t("Counter Documents")} ${t("Extracting")}`,
        value: stats.extractingDocuments,
        filterKey: "iExtractingDocs",
        statsType,
      },
      {
        label: `${t("Counter Documents")} ${t("Reviewing")}`,
        value: stats.reviewingDocuments,
        filterKey: "iReviewingDocs",
        statsType,
      },
    ];
  }, [t]);

  // Cartes globales et filtrées
  const globalFixedCards = useMemo(() => createFixedCards(globalStats, lots.length, "global"), [createFixedCards, globalStats, lots.length]);
  const filteredFixedCards = useMemo(() => createFixedCards(filteredStats, filteredData.length, "filtered"), [createFixedCards, filteredStats, filteredData.length]);

  // Cartes de statuts globales et filtrées
  const globalStatusCards = useMemo(() => 
    Object.entries(statusCountsGlobal).map(([status, count]) => ({
      label: t(status),
      value: count,
      filterKey: status,
      statsType: "global",
    })),
    [statusCountsGlobal, t]
  );

  // Stats filtrées - SEULEMENT ces 6 items
  const filteredFixedCardsOnly = useMemo(() => {
    const cards = [
      {
        label: `${t("Total")} ${t("Lots")}`,
        value: filteredData.length,
        filterKey: null,
        statsType: "filtered",
      },
      {
        label: `${t("Counter Documents")} ${t("Error")}`,
        value: filteredStats.errorDocuments,
        filterKey: "iErroredDocs",
        statsType: "filtered",
      },
      {
        label: `${t("Counter Documents")} ${t("Invalids")}`,
        value: filteredStats.invalidDocuments,
        filterKey: "iInvalidDocs",
        statsType: "filtered",
      },
      {
        label: `${t("Counter Documents")} ${t("Suspect")}`,
        value: filteredStats.suspectDocuments,
        filterKey: "iSuspectDocs",
        statsType: "filtered",
      },
      {
        label: `${t("Counter Documents")} ${t("Extracting")}`,
        value: filteredStats.extractingDocuments,
        filterKey: "iExtractingDocs",
        statsType: "filtered",
      },
      {
        label: `${t("Counter Documents")} ${t("Reviewing")}`,
        value: filteredStats.reviewingDocuments,
        filterKey: "iReviewingDocs",
        statsType: "filtered",
      },
    ];
    return cards;
  }, [filteredData.length, filteredStats, t]);

  // Graphique global
  const chartDataGlobal = useMemo(() => ({
    labels: Object.keys(statusCountsGlobal),
    datasets: [
      {
        label: t("Nombre de lot"),
        data: Object.values(statusCountsFiltered),
        backgroundColor: [
          "rgba(46, 204, 113, 0.8)",
          "rgba(52, 152, 219, 0.8)",
          "rgba(149, 165, 166, 0.8)",
          "rgba(241, 196, 15, 0.8)",
          "rgba(155, 89, 182, 0.8)",
          "rgba(230, 126, 34, 0.8)",
        ],
        borderColor: [
          "rgba(46, 204, 113, 1)",
          "rgba(52, 152, 219, 1)",
          "rgba(149, 165, 166, 1)",
          "rgba(241, 196, 15, 1)",
          "rgba(155, 89, 182, 1)",
          "rgba(230, 126, 34, 1)",
        ],
        borderWidth: 0,
      },
    ],
  }), [statusCountsGlobal, t]);

  
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      tooltip: { enabled: true },
    },
    scales: {
      x: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  return (
    <div id="lotstat">
  <div id="lotstat_menu">
    <p>{t("Statistics")}</p>
  </div>

  {/* GLOBAL */}
  <div className="lotstat_section">
    <div className="lotstat_section_title">
      <h3>{t("Global Statistics")}</h3>
    </div>

    <div className="global-stats-cadres">
      <div className="global-stats-chart">
        <Bar data={chartDataGlobal} options={options} />
      </div>

      <div className="global-stats-cards">
        {[...globalStatusCards, ...globalFixedCards].map((card, i) => (
          <div
            key={i}
            className="counter-card global-counter-card"
            onClick={() => onFilterClick?.(card.filterKey ?? "")}
          >
            <p>{card.label}</p>
            <h3>{card.value}</h3>
          </div>
        ))}
      </div>
    </div>
  </div>

  {/* FILTERED */}
  {hasFiltersApplied && (
    <div className="lotstat_section">
      <div className="lotstat_section_title">
        <h3>{t("Filtered Statistics")}</h3>
      </div>

      <div className="filtered-stats-cadres">
        <div className="filtered-stats-cards">
          {filteredFixedCardsOnly.map((card, i) => (
            <div key={i} className="filtered-counter-card">
              <p>{card.label}</p>
              <h3>{card.value}</h3>
            </div>
          ))}
        </div>
      </div>
    </div>
  )}
</div>
  );
};

export default LotStat;
